import { formatTimestamp } from 'Molecules/HistoryItem/HistoryHelpers';
import { TableSpecificDataType } from './History.hooks';
import {
  AuditLogEntryFieldsFragment_,
  EsrsAssessmentContextQuery_,
  QuestionType_Enum_,
} from 'models';

export const esrsAuditLogsCases = ({
  contextData,
  auditLog,
  isMetric,
}: {
  contextData: EsrsAssessmentContextQuery_['EsrsAssessment_by_pk'];
  auditLog: AuditLogEntryFieldsFragment_;
  isMetric?: boolean;
}): TableSpecificDataType => {
  const { table, timestamp, newData, oldData } = auditLog;
  let contextualInfo = '';
  let metaInfo: { [key: string]: string } = {};
  let dataChanges = null;
  let operation =
    auditLog?.operation === 'UPDATE' && oldData?.deletedAt === null && newData?.deletedAt !== null
      ? 'DELETE'
      : auditLog?.operation;
  let withReplacement = false;

  switch (table) {
    case 'Datapoint': {
      dataChanges = [];

      const reportingUnit = contextData?.reportingUnits.find((ru) =>
        ru.answers.some((ans) => ans.id === newData.answerId)
      );

      const metric = reportingUnit?.answers.find((ans) => ans.id === newData.answerId)?.metric;

      const standard = metric?.requirement.group.standard;

      const metricTitle = metric?.shortTitle ?? metric?.title ?? '';
      contextualInfo = isMetric ? '' : metricTitle;

      if (
        oldData?.factValue !== newData?.factValue &&
        newData !== null &&
        newData?.factValue !== null
      ) {
        dataChanges.push({
          oldData: !!oldData?.factValue ? oldData?.factValue : undefined,
          newData: !!newData?.factValue ? newData?.factValue : undefined,
          questionType:
            metric?.metricType === 'LONG_TEXT'
              ? QuestionType_Enum_.LongText_
              : QuestionType_Enum_.Decimal_,
        });
      } else {
        dataChanges.push({
          oldData: !!oldData?.value ? oldData?.value : undefined,
          newData: !!newData?.value ? newData?.value : undefined,
          questionType:
            metric?.metricType === 'LONG_TEXT'
              ? QuestionType_Enum_.LongText_
              : QuestionType_Enum_.Decimal_,
        });
      }

      metaInfo = isMetric
        ? { timestamp: formatTimestamp(timestamp) }
        : {
            timestamp: formatTimestamp(timestamp),
            standardName: `${standard?.title} (${standard?.reference})`,
            drName: `${metric?.requirement.title} (${metric?.requirement.reference})`,
            metricType:
              metric?.metricType === QuestionType_Enum_.Decimal_
                ? 'Decimal'
                : metric?.metricType === QuestionType_Enum_.YesNo_
                  ? 'Boolean'
                  : 'Narrative',
            reportingUnit: reportingUnit?.name ?? '',
          };
      withReplacement = true;
      break;
    }
    case 'Answer': {
      dataChanges = [];
      const reportingUnit = contextData?.reportingUnits.find((ru) =>
        ru.answers.some((ans) => ans.metricRef === newData.metricRef)
      );

      const metric = reportingUnit?.answers.find(
        (ans) => ans.metricRef === newData.metricRef
      )?.metric;

      const standard = metric?.requirement.group.standard;

      const metricTitle = metric?.shortTitle ?? metric?.title ?? '';
      contextualInfo = isMetric ? '' : metricTitle;

      operation = 'OPTOUT';

      dataChanges.push({
        oldData: !!oldData?.optOutReason ? oldData?.optOutReason : undefined,
        newData: !!newData?.optOutReason ? newData?.optOutReason : undefined,
        questionType:
          metric?.metricType === 'LONG_TEXT'
            ? QuestionType_Enum_.LongText_
            : QuestionType_Enum_.Decimal_,
      });

      metaInfo = isMetric
        ? { timestamp: formatTimestamp(timestamp) }
        : {
            timestamp: formatTimestamp(timestamp),
            standardName: `${standard?.title} (${standard?.reference})`,
            drName: `${metric?.requirement.title} (${metric?.requirement.reference})`,
            metricType:
              metric?.metricType === QuestionType_Enum_.Decimal_
                ? 'Decimal'
                : metric?.metricType === QuestionType_Enum_.YesNo_
                  ? 'Boolean'
                  : 'Narrative',
            reportingUnit: reportingUnit?.name ?? '',
          };
      withReplacement = true;
      break;
    }

    case 'DatapointMdrm': {
      dataChanges = [];

      const reportingUnit = contextData?.reportingUnits.find((ru) =>
        ru.answers.some((ans) => ans.datapoints.some((dp) => dp.id === newData.datapointId))
      );

      const metric = contextData?.reportingUnits
        .find((ru) =>
          ru.answers.some((ans) => ans.datapoints.some((dp) => dp.id === newData.datapointId))
        )
        ?.answers.find((ans) => ans.datapoints.some((dp) => dp.id === newData.datapointId))?.metric;

      const standard = metric?.requirement.group.standard;

      const metricTitle = metric?.shortTitle ?? metric?.title ?? '';
      contextualInfo = isMetric ? '' : metricTitle;

      dataChanges.push({
        oldData: !!oldData?.value ? oldData?.value : undefined,
        newData: !!newData?.value ? newData?.value : undefined,
        questionType:
          metric?.metricType === 'LONG_TEXT'
            ? QuestionType_Enum_.LongText_
            : QuestionType_Enum_.Decimal_,
      });

      metaInfo = isMetric
        ? { timestamp: formatTimestamp(timestamp) }
        : {
            timestamp: formatTimestamp(timestamp),
            standardName: `${standard?.title} (${standard?.reference})`,
            drName: `${metric?.requirement.title} (${metric?.requirement.reference})`,
            metricType:
              metric?.metricType === QuestionType_Enum_.Decimal_
                ? 'Decimal'
                : metric?.metricType === QuestionType_Enum_.YesNo_
                  ? 'Boolean'
                  : 'Narrative',
            reportingUnit: reportingUnit?.name ?? '',
          };
      break;
    }

    case 'DatapointChoice': {
      dataChanges = [];

      const reportingUnit = contextData?.reportingUnits.find((ru) =>
        ru.answers.some((ans) => ans.datapoints.some((dp) => dp.id === newData.datapointId))
      );

      const answer = reportingUnit?.answers.find((ans) =>
        ans.datapoints.some((dp) => dp.id === newData.datapointId)
      );

      const metric = answer?.metric;

      const standard = metric?.requirement.group.standard;

      const metricTitle = metric?.shortTitle ?? metric?.title ?? '';
      contextualInfo = isMetric ? '' : metricTitle;

      const oldChoice = answer?.datapoints
        .find((dp) => dp.id === oldData?.datapointId)
        ?.datapointChoices.find((choice) => choice.choice.reference === oldData?.choiceRef)
        ?.choice.title;

      const newChoice = answer?.datapoints
        .find((dp) => dp.id === newData?.datapointId)
        ?.datapointChoices.find((choice) => choice.choice.reference === newData?.choiceRef)
        ?.choice.title;

      dataChanges.push({
        oldData: !!oldChoice ? oldChoice : undefined,
        newData: !!newChoice ? newChoice : undefined,
      });

      metaInfo = isMetric
        ? { timestamp: formatTimestamp(timestamp) }
        : {
            timestamp: formatTimestamp(timestamp),
            standardName: `${standard?.title} (${standard?.reference})`,
            drName: `${metric?.requirement.title} (${metric?.requirement.reference})`,
            metricType:
              metric?.metricType === QuestionType_Enum_.Decimal_
                ? 'Decimal'
                : metric?.metricType === QuestionType_Enum_.YesNo_
                  ? 'Boolean'
                  : 'Narrative',
            reportingUnit: reportingUnit?.name ?? '',
          };
      break;
    }
  }
  return { contextualInfo, metaInfo, dataChanges, operation, withReplacement };
};
